/**
 * RareScale.jsx — Complete 4-act rare disease magnitude page
 *
 * Act 1: Dot particle field — 300M people
 * Act 2: Disease bubble chart — 7,000 conditions
 * Act 3: Gene therapy overlap network
 * Act 4: Approval timeline — accelerating momentum
 *
 * Install: npm install gsap
 */

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ContactFooter from '../components/ContactFooter';

gsap.registerPlugin(ScrollTrigger);

// ─── FONTS ────────────────────────────────────────────────────────────────────
const FONT_URL =
  'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=IBM+Plex+Mono:wght@300;400;500&family=Syne:wght@400;500;700&display=swap';

// ─── ACT 1 COPY ───────────────────────────────────────────────────────────────
const ACT1_LINES = [
  { at: 0,    text: 'This is one person.' },
  { at: 0.10, text: "They woke up this morning not knowing what was wrong with them.\nThey've seen eleven doctors. It took four years to get a name for what they have.\nThere are fewer than 400 people in the world with the same diagnosis." },
  { at: 0.25, text: 'They are not alone\nin being alone.' },
  { at: 0.42, text: 'Rare diseases are defined as conditions affecting fewer than 1 in 2,000 people.\nBy that definition, there are over 7,000 of them.' },
  { at: 0.60, text: 'Each dot is a person.\nA child, mostly — 70% of rare diseases begin in childhood.\nA parent watching and waiting. A clinician without answers.' },
  { at: 0.82, text: '300 million people worldwide\nlive with a rare disease.\n\nThat is larger than the entire population of the United States.\nRare, collectively, is not rare at all.' },
];

// ─── ACT 2: DISEASES ──────────────────────────────────────────────────────────
const DISEASES = [
  { name: 'Cystic Fibrosis',         patients: 180000, color: '#00e5b0', category: 'Pulmonary'    },
  { name: "Huntington's",            patients: 30000,  color: '#ff6b8a', category: 'Neurological' },
  { name: 'Duchenne MD',             patients: 15000,  color: '#b48cfa', category: 'Muscular'     },
  { name: 'Spinal Muscular Atrophy', patients: 10000,  color: '#00e5b0', category: 'Neurological' },
  { name: 'PKU',                     patients: 16500,  color: '#f5a623', category: 'Metabolic'    },
  { name: 'Gaucher Disease',         patients: 20000,  color: '#f5a623', category: 'Metabolic'    },
  { name: 'Fabry Disease',           patients: 50000,  color: '#f5a623', category: 'Metabolic'    },
  { name: 'Hemophilia A',            patients: 80000,  color: '#ff6b8a', category: 'Hematologic'  },
  { name: 'Hemophilia B',            patients: 30000,  color: '#ff6b8a', category: 'Hematologic'  },
  { name: 'Sickle Cell',             patients: 100000, color: '#ff6b8a', category: 'Hematologic'  },
  { name: 'Beta-Thalassemia',        patients: 60000,  color: '#ff6b8a', category: 'Hematologic'  },
  { name: 'Rett Syndrome',           patients: 15000,  color: '#b48cfa', category: 'Neurological' },
  { name: 'Pompe Disease',           patients: 10000,  color: '#f5a623', category: 'Metabolic'    },
  { name: 'ALS',                     patients: 30000,  color: '#b48cfa', category: 'Neurological' },
  { name: 'Marfan Syndrome',         patients: 200000, color: '#5bc8f5', category: 'Connective'   },
  { name: 'Osteogenesis Imperfecta', patients: 50000,  color: '#5bc8f5', category: 'Connective'   },
  { name: "Wilson's Disease",        patients: 10000,  color: '#f5a623', category: 'Metabolic'    },
  { name: 'Prader-Willi',            patients: 20000,  color: '#b48cfa', category: 'Neurological' },
  { name: 'Angelman Syndrome',       patients: 20000,  color: '#b48cfa', category: 'Neurological' },
  { name: 'MCAD Deficiency',         patients: 25000,  color: '#f5a623', category: 'Metabolic'    },
  { name: "Friedreich's Ataxia",     patients: 15000,  color: '#b48cfa', category: 'Neurological' },
  { name: 'MPS I (Hurler)',          patients: 3500,   color: '#f5a623', category: 'Metabolic'    },
  { name: 'Limb-Girdle MD',                   patients: 7000,  color: '#b48cfa', category: 'Muscular'     },
  { name: 'Pyridoxine-Dependent Epilepsy',   patients: 1200,  color: '#f5a623', category: 'Metabolic'    },
];

const CATEGORY_COLORS = {
  Neurological: '#b48cfa',
  Metabolic:    '#f5a623',
  Hematologic:  '#ff6b8a',
  Pulmonary:    '#00e5b0',
  Muscular:     '#5bc8f5',
  Connective:   '#5bc8f5',
};

// ─── ACT 3: THERAPY PLATFORMS ─────────────────────────────────────────────────
// Each platform node connects to disease nodes via shared mechanism
const PLATFORMS = [
  {
    id: 'aav',
    name: 'AAV\nGene Therapy',
    color: '#00e5b0',
    x: 0.5, y: 0.22,
    proof: 'Zolgensma (SMA, 2019) — first $2M one-time cure',
    desc: 'Adeno-associated viral vectors deliver a corrected gene copy directly into cells. Originally perfected for SMA, the same delivery vehicle now carries payloads for a dozen other diseases.',
    diseases: ['Spinal Muscular Atrophy', 'Duchenne MD', 'Hemophilia A', 'Hemophilia B', 'Pompe Disease', "Friedreich's Ataxia", 'Pyridoxine-Dependent Epilepsy'],
  },
  {
    id: 'aso',
    name: 'Antisense\nOligonucleotides',
    color: '#b48cfa',
    x: 0.18, y: 0.55,
    proof: 'Spinraza (SMA, 2016) — first ASO for neurological disease',
    desc: 'Short synthetic DNA strands that intercept and correct faulty RNA before it produces a broken protein. Proven in SMA, now targeting Huntington\'s, ALS, and more.',
    diseases: ['Spinal Muscular Atrophy', "Huntington's", 'ALS', 'Duchenne MD'],
  },
  {
    id: 'crispr',
    name: 'CRISPR\nEditing',
    color: '#f5a623',
    x: 0.82, y: 0.55,
    proof: 'Casgevy (Sickle Cell + Beta-Thal, 2023) — first CRISPR therapy approved worldwide',
    desc: 'Molecular scissors that cut and correct DNA at a precise location. One approval covered two distinct diseases simultaneously — the same edit, two patient populations.',
    diseases: ['Sickle Cell', 'Beta-Thalassemia', 'Duchenne MD', "Huntington's"],
  },
  {
    id: 'ert',
    name: 'Enzyme\nReplacement',
    color: '#ff6b8a',
    x: 0.18, y: 0.82,
    proof: 'Ceredase (Gaucher, 1991) — proof the model works',
    desc: 'Replace the missing enzyme directly. Proven in Gaucher disease in 1991, the same logic has since been applied to Fabry, Pompe, and six MPS disorders. A concept proven once, applied many times.',
    diseases: ['Gaucher Disease', 'Fabry Disease', 'Pompe Disease', 'MPS I (Hurler)'],
  },
  {
    id: 'mrna',
    name: 'mRNA /\nLNP Delivery',
    color: '#5bc8f5',
    x: 0.82, y: 0.82,
    proof: 'COVID-19 vaccines proved the platform at global scale',
    desc: 'Lipid nanoparticles deliver mRNA instructions directly to cells. The COVID vaccines demonstrated safety and scale. The same delivery system is now in trials for propionic acidemia and methylmalonic acidemia.',
    diseases: ['MCAD Deficiency', 'PKU', 'Fabry Disease', 'Pyridoxine-Dependent Epilepsy'],
  },
];

// ─── ACT 4: APPROVAL TIMELINE ─────────────────────────────────────────────────
const APPROVALS = [
  { year: 1983, label: 'Orphan Drug Act',                note: 'US legislation creates financial incentives for rare disease research',         color: '#5a637a', type: 'policy'  },
  { year: 1991, label: 'Ceredase — Gaucher',             note: 'First enzyme replacement therapy for any lysosomal storage disorder',          color: '#f5a623', type: 'therapy' },
  { year: 1993, label: 'Cerezyme — Gaucher',             note: 'Recombinant ERT — cheaper, more scalable, same principle',                     color: '#f5a623', type: 'therapy' },
  { year: 2001, label: 'Fabrazyme — Fabry',              note: 'ERT logic applied to a second lysosomal storage disorder',                     color: '#f5a623', type: 'therapy' },
  { year: 2006, label: 'Myozyme — Pompe',                note: 'ERT for Pompe disease, saving infants with cardiac involvement',               color: '#f5a623', type: 'therapy' },
  { year: 2016, label: 'Spinraza — SMA',                 note: 'First antisense oligonucleotide for a neurological disease',                   color: '#b48cfa', type: 'therapy' },
  { year: 2017, label: 'Luxturna — Retinal Dystrophy', note: 'First in vivo gene therapy approved in the US',                     color: '#00e5b0', type: 'therapy' },
  { year: 2019, label: 'Zolgensma — SMA',            note: 'First AAV gene therapy. One dose. One time. $2.1M.',                  color: '#00e5b0', type: 'therapy' },
  { year: 2019, label: 'Givlaari — AHP',             note: 'First RNAi therapy for a rare metabolic disorder',                    color: '#5bc8f5', type: 'therapy' },
  { year: 2021, label: 'Lumakras — KRAS cancers',                    note: 'Targeting "undruggable" mutations — opens new rare cancer categories',                         color: '#5bc8f5', type: 'therapy' },
  { year: 2022, label: 'Hemgenix — Hemophilia B',                    note: 'Gene therapy: one infusion replaces a lifetime of factor injections',                         color: '#00e5b0', type: 'therapy' },
  { year: 2022, label: 'PDE Natural History Study',                  note: 'International PDE Consortium publishes largest natural history dataset for ALDH7A1-PDE — foundation for AAV gene therapy IND filing', color: '#f5a623', type: 'policy'  },
  { year: 2023, label: 'Casgevy — Sickle Cell',                      note: 'First CRISPR therapy approved anywhere in the world',                                        color: '#f5a623', type: 'therapy' },
  { year: 2023, label: 'Casgevy — Beta-Thalassemia',                 note: 'Same CRISPR edit. Second disease. Approved simultaneously.',                                 color: '#f5a623', type: 'therapy' },
  { year: 2023, label: 'Tofersen — ALS (SOD1)',                      note: 'ASO therapy for a specific ALS gene mutation — platform from SMA',                          color: '#b48cfa', type: 'therapy' },
  { year: 2024, label: 'Elevidys — Duchenne MD',                     note: 'Accelerated AAV gene therapy approval for DMD in young boys',                               color: '#00e5b0', type: 'therapy' },
  { year: 2024, label: 'Alhemo — PH1',                               note: 'RNAi silencing for a rare kidney stone disease — new category',                             color: '#5bc8f5', type: 'therapy' },
];

// ─── SOURCES & NOTES ──────────────────────────────────────────────────────────
const SOURCES = [
  {
    claim: '300 million people live with a rare disease; cumulative point prevalence 3.5–5.9%',
    cite: 'Nguengang Wakap S, et al. Estimating cumulative point prevalence of rare diseases: analysis of the Orphanet database. Eur J Hum Genet, 2020.',
    url: 'https://www.nature.com/articles/s41431-019-0508-0',
  },
  {
    claim: 'Over 7,000 known rare diseases; new conditions described regularly',
    cite: 'NIH Genetic and Rare Diseases Information Center (GARD); Orphanet Report Series.',
    url: 'https://rarediseases.info.nih.gov/',
  },
  {
    claim: 'Rare disease defined as affecting fewer than 1 in 2,000 people (EU definition)',
    cite: 'European Commission — Regulation (EC) No 141/2000 on orphan medicinal products. (US: fewer than 200,000 persons, Orphan Drug Act 1983.)',
    url: 'https://health.ec.europa.eu/medicinal-products/orphan-medicinal-products_en',
  },
  {
    claim: 'Roughly 70% of rare diseases begin in childhood',
    cite: 'EURORDIS — Rare Diseases Europe; Rare Diseases International.',
    url: 'https://www.eurordis.org/information-support/what-is-a-rare-disease/',
  },
  {
    claim: '~95% of rare diseases have no FDA-approved treatment',
    cite: 'National Organization for Rare Disorders (NORD); EveryLife Foundation for Rare Diseases.',
    url: 'https://rarediseases.org/',
  },
  {
    claim: 'Therapy approvals and dates (Ceredase, Spinraza, Zolgensma, Casgevy, Elevidys, etc.)',
    cite: 'U.S. Food & Drug Administration — drug approval records & Orphan Drug designations.',
    url: 'https://www.accessdata.fda.gov/scripts/cder/daf/',
  },
  {
    claim: 'Orphan Drug Act incentives for rare disease research (1983)',
    cite: 'Orphan Drug Act of 1983, Pub. L. 97-414 (United States).',
    url: 'https://www.fda.gov/industry/medical-products-rare-diseases-and-conditions/orphan-drug-act-relevant-excerpts',
  },
  {
    claim: 'US patient population estimates for individual diseases',
    cite: 'Compiled from NORD rare disease database and disease-specific patient foundations; figures are approximate.',
    url: null,
  },
];

// ─── UTILS ────────────────────────────────────────────────────────────────────
function formatCount(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}K`;
  return n.toString();
}

function bubbleRadius(patients) {
  return Math.max(28, Math.min(90, Math.sqrt(patients / 180000) * 90));
}

// Category regions — 3 columns × 2 rows. Each category cluster gets its own
// region so the staged reveal feels spatial: Hematologic appears in one zone,
// Metabolic in another, etc.
const CATEGORY_REGIONS = {
  Hematologic:  { cx: 0.18, cy: 0.32 },
  Metabolic:    { cx: 0.50, cy: 0.32 },
  Neurological: { cx: 0.82, cy: 0.32 },
  Pulmonary:    { cx: 0.18, cy: 0.72 },
  Muscular:     { cx: 0.50, cy: 0.72 },
  Connective:   { cx: 0.82, cy: 0.72 },
};

function layoutBubbles(diseases, W, H) {
  // Group diseases by category and pack each group inside its assigned region.
  const byCategory = {};
  diseases.forEach((d) => {
    (byCategory[d.category] ||= []).push(d);
  });

  const result = [];
  const regionRX = W * 0.14;
  const regionRY = H * 0.18;

  Object.entries(byCategory).forEach(([cat, list]) => {
    const region = CATEGORY_REGIONS[cat] || { cx: 0.5, cy: 0.5 };
    const cx = W * region.cx;
    const cy = H * region.cy;
    const n  = list.length;
    list.forEach((d, i) => {
      const r = bubbleRadius(d.patients);
      // Lay out within the region: large bubbles near center, smaller ones orbiting
      let x, y;
      if (n === 1) {
        x = cx; y = cy;
      } else {
        // Sort effectively by index — biggest gets center, rest distributed
        const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
        const dist  = i === 0 ? 0 : 1;
        x = cx + Math.cos(angle) * regionRX * dist * 0.7;
        y = cy + Math.sin(angle) * regionRY * dist * 0.7;
      }
      result.push({ ...d, x, y, r });
    });
  });
  return result;
}

// ─── BUBBLE REVEAL ORDER ──────────────────────────────────────────────────────
// Categories revealed sequentially during the bubble phase. Order picked so each
// category gets its own scroll segment.
const BUBBLE_REVEAL_ORDER = ['Hematologic', 'Metabolic', 'Neurological', 'Pulmonary', 'Muscular', 'Connective'];

// Per-category dot colors (rgba prefix) — one entry per BUBBLE_REVEAL_ORDER index.
const DOT_COLORS_BY_CAT = [
  'rgba(255,107,138,', // Hematologic — pink
  'rgba(245,166,35,',  // Metabolic — orange
  'rgba(180,140,250,', // Neurological — purple
  'rgba(0,229,176,',   // Pulmonary — green
  'rgba(91,200,245,',  // Muscular — blue
  'rgba(91,200,245,',  // Connective — blue (same)
];

// Stable per-render target points for dot pull — match CATEGORY_REGIONS so dots
// fly toward their bubble cluster's center.
const STABLE_DOT_TARGETS = BUBBLE_REVEAL_ORDER.map((cat) => {
  const region = CATEGORY_REGIONS[cat] || { cx: 0.5, cy: 0.5 };
  return { x: region.cx, y: region.cy };
});

// ─── DOT ARRANGEMENT TARGETS ──────────────────────────────────────────────────
// Each dot has 6 target positions, one per ACT1_LINES text moment. As scroll
// progresses, dots gently lerp between consecutive arrangements. Off-screen
// targets (-1,-1) hide a dot for that phase, so the dot count visually grows
// as the story expands. The arrangements visually echo each text line:
//
//   0: "This is one person"        →  one dot, centered
//   1: "Eleven doctors, four years" →  a tight ring around the center dot
//   2: "Not alone in being alone"  →  many small isolated clusters scattered
//   3: "7,000 conditions"          →  a wide horizontal band, condition-spectrum
//   4: "Each dot is a person"      →  uniform field across most of the canvas
//   5: "300 million worldwide"     →  full density, ready to flow into Act 2
function generateDotArrangements(numDots) {
  let seed = 7531;
  const rand = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };

  // ~36 small cluster centers for Phase 2. First is the centered one
  // (continuity from Phase 1's ring).
  const numClusters = 38;
  const clusters = [{ cx: 0.5, cy: 0.5 }];
  for (let i = 1; i < numClusters; i++) {
    clusters.push({ cx: 0.08 + rand() * 0.84, cy: 0.13 + rand() * 0.74 });
  }

  const result = [];
  for (let i = 0; i < numDots; i++) {
    // Each dot has a fixed "off-side" — all its off-screen positions are on
    // that side, so the blend between two off-screen targets stays off-screen.
    const offSide = i % 4;
    const offX = (() => {
      if (offSide === 0) return -0.10;
      if (offSide === 1) return 1.10;
      return 0.05 + rand() * 0.90;
    })();
    const offY = (() => {
      if (offSide === 2) return -0.10;
      if (offSide === 3) return 1.10;
      return 0.05 + rand() * 0.90;
    })();
    const off = { x: offX, y: offY, hidden: true };

    const phases = [];

    // PHASE 0 — solitary anchor dot
    phases.push(i === 0 ? { x: 0.5, y: 0.5 } : off);

    // PHASE 1 — anchor + ring of ~24 doctors orbiting
    if (i === 0) {
      phases.push({ x: 0.5, y: 0.5 });
    } else if (i < 25) {
      const a = ((i - 1) / 24) * Math.PI * 2;
      const r = 0.06 + rand() * 0.02;
      phases.push({ x: 0.5 + Math.cos(a) * r, y: 0.5 + Math.sin(a) * r });
    } else {
      phases.push(off);
    }

    // PHASE 2 — ~280 dots clustered into many small isolated groups
    if (i < 280) {
      const ci = i < 25 ? 0 : 1 + ((i - 25) % (numClusters - 1));
      const c = clusters[ci];
      const a = rand() * Math.PI * 2;
      const r = 0.005 + rand() * 0.025;
      phases.push({ x: c.cx + Math.cos(a) * r, y: c.cy + Math.sin(a) * r });
    } else {
      phases.push(off);
    }

    // PHASE 3 — ~1100 dots in a wide horizontal band (spectrum of conditions)
    if (i < 1100) {
      phases.push({ x: 0.04 + rand() * 0.92, y: 0.42 + (rand() - 0.5) * 0.32 });
    } else {
      phases.push(off);
    }

    // PHASE 4 — ~2300 dots in a broader uniform-ish field
    if (i < 2300) {
      phases.push({ x: 0.03 + rand() * 0.94, y: 0.06 + rand() * 0.88 });
    } else {
      phases.push(off);
    }

    // PHASE 5 — all 3200 dots pre-clustered around each dot's category region.
    // The "300M" moment looks like 6 overlapping disease-family clouds, which
    // means the Act 2 bubble pull is just a gentle tightening (short distance)
    // instead of a long cross-screen journey.
    const cat = i % 6;
    const region = STABLE_DOT_TARGETS[cat] || { x: 0.5, y: 0.5 };
    const cloudAngle = rand() * Math.PI * 2;
    // sqrt for uniform-area density; max radius keeps clouds overlapping
    const cloudR = 0.03 + Math.sqrt(rand()) * 0.24;
    phases.push({
      x: Math.max(0.02, Math.min(0.98, region.x + Math.cos(cloudAngle) * cloudR)),
      y: Math.max(0.04, Math.min(0.96, region.y + Math.sin(cloudAngle) * cloudR * 1.05)),
    });

    result.push(phases);
  }
  return result;
}

// ─── DOT CANVAS ───────────────────────────────────────────────────────────────
// Scrollytelling dots in Pudding-cool style. Each dot has 6 arrangement targets
// (above). As `arrangementPhase`/`arrangementBlend` change, dots lerp toward
// the blended target with a per-dot ease rate for organic stagger.
//
// In Act 2 (when catPulls > 0), dots leave their arrangement and pull into
// the disease bubble clusters.
//
// Props:
//   arrangementPhase  — 0..5, current ACT1_LINES index
//   arrangementBlend  — 0..1, transition progress to the next phase
//   catPulls          — [0..1] x 6, Act 2 pull amounts (0 = follow arrangement)
//   targets           — Act 2 category target positions
function DotCanvas({ arrangementPhase = 0, arrangementBlend = 0, catPulls = [0,0,0,0,0,0], targets }) {
  const canvasRef   = useRef(null);
  const dotsRef     = useRef([]);
  const rafRef      = useRef(null);
  const phaseRef    = useRef(arrangementPhase);
  const blendRef    = useRef(arrangementBlend);
  const pullsRef    = useRef(catPulls);
  const targetsRef  = useRef(targets);
  const DOT_COUNT   = 3200;

  useEffect(() => { phaseRef.current = arrangementPhase; }, [arrangementPhase]);
  useEffect(() => { blendRef.current = arrangementBlend; }, [arrangementBlend]);
  useEffect(() => { pullsRef.current = catPulls; }, [catPulls]);
  useEffect(() => {
    targetsRef.current = targets;
    if (targets && dotsRef.current.length) {
      dotsRef.current.forEach((d) => {
        const t = targets[d.cat] || { x: 0.5, y: 0.5 };
        d.tx = t.x + d.tjx;
        d.ty = t.y + d.tjy;
      });
    }
  }, [targets]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      canvas.width  = canvas.offsetWidth  * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    };
    resize();
    window.addEventListener('resize', resize);

    const arrangements = generateDotArrangements(DOT_COUNT);
    const initialTargets = targetsRef.current || Array(6).fill({ x: 0.5, y: 0.5 });

    dotsRef.current = arrangements.map((arr, i) => {
      const cat = i % 6;  // even category distribution
      const tjx = (((i * 31) % 100) / 100 - 0.5) * 0.10;
      const tjy = (((i * 47) % 100) / 100 - 0.5) * 0.12;
      const target = initialTargets[cat] || { x: 0.5, y: 0.5 };
      // Start at phase 0 arrangement (most dots off-screen)
      return {
        x: arr[0].x, y: arr[0].y,
        arr,
        // Dot 0 is the anchor — a touch larger so it reads as "one person"
        r: i === 0 ? 2.6 : (((i * 13) % 100) / 100) * 1.5 + 0.9,
        cat, tjx, tjy,
        tx: target.x + tjx,
        ty: target.y + tjy,
        // Per-dot ease rate for organic stagger (0.04-0.11) — gentle but not glacial
        lerp: 0.04 + (((i * 7) % 100) / 100) * 0.07,
      };
    });

    const ctx = canvas.getContext('2d');
    const draw = () => {
      const W = canvas.width, H = canvas.height;
      const dpr = window.devicePixelRatio;
      ctx.clearRect(0, 0, W, H);

      const phase = phaseRef.current;
      const blend = blendRef.current;
      const nextPhase = Math.min(5, phase + 1);
      const pulls = pullsRef.current;

      dotsRef.current.forEach((d) => {
        const pull = pulls[d.cat] ?? 0;
        let targetX, targetY, drawR, alpha;

        if (pull > 0) {
          // Act 2: lerp from arrangement position toward category target
          if (pull >= 1) return; // absorbed
          // Cubic ease — gentle start, soft finish — feels like a current
          // pulling dots inward rather than a snap.
          const eased = pull < 0.5
            ? 4 * pull * pull * pull
            : 1 - Math.pow(-2 * pull + 2, 3) / 2;
          // Use phase 5 arrangement as the "from" position (pre-clustered cloud)
          const from = d.arr[5];
          targetX = from.x * (1 - eased) + d.tx * eased;
          targetY = from.y * (1 - eased) + d.ty * eased;
          // Dots stay larger longer, only really shrinking near absorption
          drawR = d.r * (1 - Math.pow(pull, 2.5) * 0.55);
          // Soft fade — held bright through 60%, then fades out
          alpha = pull < 0.6 ? 0.92 : 0.92 * (1 - (pull - 0.6) / 0.4);
        } else {
          // Act 1: blend current arrangement target with the next
          const cur = d.arr[phase];
          const nxt = d.arr[nextPhase];
          // If both current and next targets are hidden, this dot stays
          // off-screen — skip entirely (avoids "flying across" artifacts
          // when off-screen positions are on opposite sides).
          if (cur.hidden && nxt.hidden) return;
          targetX = cur.x * (1 - blend) + nxt.x * blend;
          targetY = cur.y * (1 - blend) + nxt.y * blend;
          drawR = d.r;
          // Fade in/out when transitioning into/out of visibility
          if (cur.hidden && !nxt.hidden) alpha = 0.88 * blend;
          else if (!cur.hidden && nxt.hidden) alpha = 0.88 * (1 - blend);
          else alpha = 0.88;
        }

        // Smoothly ease current position toward target
        d.x += (targetX - d.x) * d.lerp;
        d.y += (targetY - d.y) * d.lerp;

        // Skip render if off-screen or too faint
        if (d.x < -0.04 || d.x > 1.04 || d.y < -0.04 || d.y > 1.04) return;
        if (alpha < 0.02 || drawR < 0.15) return;

        ctx.beginPath();
        ctx.arc(d.x * W, d.y * H, drawR * dpr, 0, Math.PI * 2);
        ctx.fillStyle = DOT_COLORS_BY_CAT[d.cat] + alpha.toFixed(3) + ')';
        ctx.fill();
      });

      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(rafRef.current); };
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />;
}

// ─── BUBBLE TRANSFORM (dots fade, scale into disease bubbles) ─────────────────
function BubbleTransform({ transformProgress, highlightCategory }) {
  const svgRef    = useRef(null);
  const layoutRef = useRef(null);
  const [dims, setDims] = useState({ w: 1200, h: 800 });

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;
    const updateDims = () => {
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      if (w > 0 && h > 0) setDims({ w, h });
    };
    updateDims();
    const ro = new ResizeObserver(updateDims);
    ro.observe(parent);
    return () => ro.disconnect();
  }, []);

  if (!layoutRef.current || layoutRef.current._w !== dims.w) {
    layoutRef.current = layoutBubbles(DISEASES, dims.w, dims.h);
    layoutRef.current._w = dims.w;
  }
  const laid = layoutRef.current;

  // Unique gradient ids per category color
  const uniqueColors = [...new Set(DISEASES.map(d => d.color))];

  return (
    <svg ref={svgRef} viewBox={`0 0 ${dims.w} ${dims.h}`}
         preserveAspectRatio="xMidYMid meet"
         style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      <defs>
        {uniqueColors.flatMap((c) => {
          const id = c.replace('#', '');
          return [
            // Matte fill — gentle vertical falloff, no centered hotspot.
            // Top slightly lighter than bottom for a hint of dimension only.
            <linearGradient key={`b-${id}`} id={`bubble-${id}`} x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%"   stopColor={c} stopOpacity="0.26" />
              <stop offset="100%" stopColor={c} stopOpacity="0.16" />
            </linearGradient>,
          ];
        })}
      </defs>

      {laid.map((d, i) => {
        const isHL = !highlightCategory || d.category === highlightCategory;
        const N = BUBBLE_REVEAL_ORDER.length;
        const categoryIdx = BUBBLE_REVEAL_ORDER.indexOf(d.category);
        const slotWidth = 1 / N;
        const categoryStart = categoryIdx * slotWidth;
        const categoryProgress = Math.max(0, Math.min(1, (transformProgress - categoryStart) / slotWidth));
        // Ease-out: fade in faster at first, then settle — gives the bubble a
        // "presence" that the dots converge into.
        const bubbleEase = 1 - Math.pow(1 - categoryProgress, 2.2);
        const bubbleOpacity = bubbleEase;
        // Slightly more dramatic scale-in: start smaller so the bubble has
        // visible room to "grow" as dots arrive into it.
        const bubbleScale = 0.55 + bubbleEase * 0.45;
        const id = d.color.replace('#', '');
        // Text shows once bubble is mostly grown to avoid awkward early labels
        const textOpacity = Math.max(0, Math.min(1, (categoryProgress - 0.5) / 0.4));
        const labelFontSize = Math.max(9, Math.min(14, d.r * 0.26));
        const countFontSize = Math.max(8, Math.min(11, d.r * 0.19));
        return (
          <g key={d.name}
             opacity={bubbleOpacity * (isHL ? 1 : 0.22)}
             transform={`translate(${d.x},${d.y}) scale(${bubbleScale}) translate(${-d.x},${-d.y})`}
             style={{ transition: 'opacity 0.3s' }}>
            {/* Soft outer halo — very subtle, more like a printed glow */}
            <circle cx={d.x} cy={d.y} r={d.r * 1.18}
              fill={d.color} opacity={isHL ? 0.04 : 0.015} />
            {/* Main bubble body — flat matte fill */}
            <circle cx={d.x} cy={d.y} r={d.r}
              fill={`url(#bubble-${id})`}
              stroke={d.color}
              strokeWidth={isHL ? 1.6 : 0.7}
              strokeOpacity={isHL ? 0.78 : 0.32}
            />

            {/* Disease name + patient count — every bubble gets both, with the
                font sized down on small bubbles so the count is always visible. */}
            {d.r > 38 ? (
              <>
                <text x={d.x} y={d.y - 2} textAnchor="middle" fontSize={labelFontSize}
                  fill="#eef2f8" fontFamily="'Syne', sans-serif" fontWeight="500"
                  opacity={textOpacity}
                  style={{ pointerEvents: 'none', letterSpacing: '0.01em' }}>
                  {d.name}
                </text>
                <text x={d.x} y={d.y + labelFontSize + 3} textAnchor="middle" fontSize={countFontSize}
                  fill={d.color} fontFamily="'IBM Plex Mono', monospace"
                  opacity={textOpacity * 0.85}
                  style={{ pointerEvents: 'none', letterSpacing: '0.08em' }}>
                  {formatCount(d.patients)}
                </text>
              </>
            ) : (
              <>
                <text x={d.x} y={d.y - 3} textAnchor="middle" fontSize="9"
                  fill="#eef2f8" fontFamily="'Syne', sans-serif" fontWeight="500"
                  opacity={textOpacity}
                  style={{ pointerEvents: 'none' }}>
                  {d.name.length > 12 ? d.name.split(' ')[0] : d.name}
                </text>
                <text x={d.x} y={d.y + 8} textAnchor="middle" fontSize="8"
                  fill={d.color} fontFamily="'IBM Plex Mono', monospace"
                  opacity={textOpacity * 0.85}
                  style={{ pointerEvents: 'none', letterSpacing: '0.06em' }}>
                  {formatCount(d.patients)}
                </text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── COUNTER ──────────────────────────────────────────────────────────────────
function Counter({ value }) {
  const displayed = Math.floor(value * 300_000_000);
  return (
    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 'clamp(1.2rem, 3vw, 2rem)', color: '#00e5b0', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
      {displayed.toLocaleString()}
      <span style={{ fontSize: '0.55em', color: '#3a4255', marginLeft: '0.5rem' }}>/ 300,000,000</span>
    </div>
  );
}

// ─── ACT 3: NETWORK GRAPH ─────────────────────────────────────────────────────
// ─── ACT 3: TREATMENT MATRIX ──────────────────────────────────────────────────
// A research-paper-style heatmap: platforms across the top, diseases down the
// side, sorted by how many platforms treat each disease. As you scroll, each
// platform column lights up — cells reveal where that platform reaches a
// disease. The compound-coverage story becomes literal: the matrix fills in.

function TreatmentMatrix({ activePlatform, revealedEdges }) {
  // Pre-compute per-disease coverage count for sorting
  const sortedDiseases = DISEASES
    .map(d => ({
      ...d,
      coverage: PLATFORMS.filter(p => p.diseases.includes(d.name)).length,
    }))
    .sort((a, b) => {
      if (b.coverage !== a.coverage) return b.coverage - a.coverage;
      return b.patients - a.patients;
    });

  // Map disease → set of treating platform ids
  const platformsByDisease = {};
  PLATFORMS.forEach(p => {
    p.diseases.forEach(d => {
      (platformsByDisease[d] ||= new Set()).add(p.id);
    });
  });

  // Short platform labels for the header
  const SHORT_LABEL = { aav: 'AAV', aso: 'ASO', crispr: 'CRISPR', ert: 'ERT', mrna: 'mRNA' };

  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column',
      fontFamily: "'IBM Plex Mono', monospace",
    }}>
      {/* Header row — platform short labels + treat counts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `minmax(0, 180px) repeat(${PLATFORMS.length}, minmax(0, 1fr))`,
        alignItems: 'end',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        paddingBottom: '0.7rem',
        marginBottom: '0.4rem',
        gap: '0.4rem',
      }}>
        <div style={{ ...S.mono, fontSize: '0.5rem', letterSpacing: '0.2em', color: '#3a4255', textTransform: 'uppercase' }}>
          Disease · patients
        </div>
        {PLATFORMS.map(p => {
          const isActive = p.id === activePlatform;
          const isRevealed = revealedEdges.includes(p.id);
          return (
            <div key={p.id} style={{
              textAlign: 'center',
              opacity: isRevealed ? 1 : 0.35,
              transition: 'opacity 0.5s',
            }}>
              <div style={{
                ...S.mono, fontWeight: 500,
                fontSize: isActive ? '0.9rem' : '0.78rem',
                color: isRevealed ? p.color : '#3a4255',
                lineHeight: 1, marginBottom: '0.3rem',
                letterSpacing: '0.05em',
                transition: 'all 0.4s',
                whiteSpace: 'nowrap',
              }}>
                {SHORT_LABEL[p.id] || p.id.toUpperCase()}
              </div>
              <div style={{ ...S.mono, fontSize: '0.5rem', letterSpacing: '0.18em', color: isActive ? p.color : '#3a4255', textTransform: 'uppercase' }}>
                · {p.diseases.length} ·
              </div>
            </div>
          );
        })}
      </div>

      {/* Body rows */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: '1px' }}>
        {sortedDiseases.map((d, rowIdx) => {
          const rowPlatforms = platformsByDisease[d.name] || new Set();
          const rowHasActive = activePlatform && rowPlatforms.has(activePlatform);
          const rowEverTreated = [...rowPlatforms].some(pid => revealedEdges.includes(pid));
          return (
            <div key={d.name} style={{
              display: 'grid',
              gridTemplateColumns: `minmax(0, 180px) repeat(${PLATFORMS.length}, minmax(0, 1fr))`,
              alignItems: 'center',
              padding: '0.25rem 0',
              gap: '0.4rem',
              background: rowHasActive ? `${PLATFORMS.find(p => p.id === activePlatform)?.color}10` : 'transparent',
              transition: 'background 0.4s',
              minHeight: 0,
            }}>
              {/* Disease name + count */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingRight: '0.5rem' }}>
                <div style={{
                  width: '3px', alignSelf: 'stretch',
                  background: d.color,
                  opacity: rowEverTreated ? 0.9 : 0.35,
                  borderRadius: '1px',
                  transition: 'opacity 0.4s',
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    ...S.syne, fontWeight: 500,
                    fontSize: '0.72rem',
                    color: rowHasActive ? '#f6f9ff' : rowEverTreated ? '#dde3f0' : '#5a637a',
                    letterSpacing: '0.01em',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    transition: 'color 0.4s',
                  }}>
                    {d.name}
                  </div>
                  <div style={{ ...S.mono, fontSize: '0.55rem', color: '#3a4255', letterSpacing: '0.06em' }}>
                    {formatCount(d.patients)}
                  </div>
                </div>
              </div>

              {/* Cells */}
              {PLATFORMS.map(p => {
                const treated = p.diseases.includes(d.name);
                const isRevealed = revealedEdges.includes(p.id);
                const isActive = p.id === activePlatform;
                const showFilled = treated && isRevealed;
                return (
                  <div key={p.id} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    {showFilled ? (
                      <div style={{
                        width: isActive ? '18px' : '14px',
                        height: isActive ? '18px' : '14px',
                        borderRadius: '50%',
                        background: p.color,
                        opacity: isActive ? 1 : 0.7,
                        boxShadow: isActive ? `0 0 10px ${p.color}88` : `0 0 4px ${p.color}55`,
                        transition: 'all 0.35s ease',
                      }} />
                    ) : treated ? (
                      // Treated but not yet revealed: tiny ghost outline
                      <div style={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        border: `1px solid ${p.color}33`,
                      }} />
                    ) : (
                      // Not treated: tiny dim dot
                      <div style={{
                        width: '3px', height: '3px', borderRadius: '50%',
                        background: '#1d2230',
                      }} />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ACT 4: TIMELINE ──────────────────────────────────────────────────────────
const TYPE_LABELS = { policy: 'Policy', therapy: 'Approval' };

function Timeline({ revealCount }) {
  const shown = APPROVALS.slice(0, revealCount);

  return (
    // Flow layout — height grows with content so the inner panel can scroll naturally.
    // Previously used position:absolute with top:% on a zero-height container, making
    // every event pile up at the top of the panel.
    <div style={{ position: 'relative', paddingBottom: '4rem' }}>
      {/* Center axis line spans full content height */}
      <div style={{
        position: 'absolute', left: '50%', top: 0, bottom: 0,
        width: '1px', background: 'rgba(255,255,255,0.06)',
        transform: 'translateX(-50%)',
      }} />

      {shown.map((a, i) => {
        const side     = i % 2 === 0 ? 'right' : 'left';
        const prevYear = i > 0 ? APPROVALS[i - 1].year : 1978;
        const yearGap  = Math.max(0, a.year - prevYear);
        // Proportional vertical spacing: more years between events = more gap
        const marginTop = i === 0 ? '1rem' : `${Math.max(14, yearGap * 22)}px`;

        return (
          <div key={`${a.year}-${a.label}`} style={{
            position: 'relative',
            display: 'flex',
            justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
            marginTop,
            opacity: 0,
            animation: 'fadeInUp 0.5s ease both',
          }}>
            {/* Dot sitting on the center axis */}
            <div style={{
              position: 'absolute', left: '50%', top: '1rem',
              width: '8px', height: '8px', borderRadius: '50%',
              background: a.color, boxShadow: `0 0 8px ${a.color}`,
              transform: 'translateX(-50%)', zIndex: 1,
            }} />

            {/* Event card on the correct side */}
            <div style={{
              width: '44%',
              padding: '0.7rem 1rem',
              background: `${a.color}10`,
              border: `1px solid ${a.color}30`,
              borderRadius: '2px',
              textAlign: side === 'right' ? 'left' : 'right',
            }}>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '0.6rem', letterSpacing: '0.1em',
                color: a.color, marginBottom: '0.2rem', textTransform: 'uppercase',
              }}>
                {a.year} · {TYPE_LABELS[a.type]}
              </div>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.85rem', color: '#dde3f0', lineHeight: 1.3, marginBottom: '0.3rem' }}>
                {a.label}
              </div>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.68rem', color: '#5a637a', lineHeight: 1.6 }}>
                {a.note}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── SHARED STYLES ────────────────────────────────────────────────────────────
const S = {
  mono: { fontFamily: "'IBM Plex Mono', monospace" },
  serif: { fontFamily: "'Instrument Serif', serif" },
  syne: { fontFamily: "'Syne', sans-serif" },
  label: { fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#00e5b0', marginBottom: '1.2rem' },
  muted: { color: '#5a637a' },
  divider: { borderTop: '1px solid rgba(255,255,255,0.05)' },
};

function Bridge({ eyebrow, heading, sub, headingColor = '#dde3f0' }) {
  return (
    <div style={{ textAlign: 'center', padding: '8rem 2rem', background: '#07080a', ...S.divider }}>
      <div style={{ ...S.label, marginBottom: '1.5rem' }}>{eyebrow}</div>
      <p style={{ ...S.serif, fontSize: 'clamp(2rem, 4vw, 3.2rem)', lineHeight: 1.15, maxWidth: '680px', margin: '0 auto 1.5rem', color: headingColor }}
        dangerouslySetInnerHTML={{ __html: heading }} />
      <p style={{ ...S.mono, fontSize: 'clamp(0.75rem, 1.2vw, 0.88rem)', ...S.muted, maxWidth: '520px', margin: '0 auto', lineHeight: 1.85 }}>
        {sub}
      </p>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function RareScale() {
  // ── Refs
  const wrapRef       = useRef(null);
  const act1Ref       = useRef(null);
  const act3Ref       = useRef(null);
  const act4Ref       = useRef(null);
  const act4PanelRef  = useRef(null);
  const copyRef       = useRef(null);
  const isFirst       = useRef(true);

  // ── State
  const [arrangementPhase,  setArrangementPhase]  = useState(0);
  const [arrangementBlend,  setArrangementBlend]  = useState(0);
  const [counterProgress,   setCounterProgress]   = useState(0);
  const [copyIndex,         setCopyIndex]          = useState(0);
  const [act1Opacity,       setAct1Opacity]        = useState(1);
  const [bubbleProgress,    setBubbleProgress]     = useState(0);
  const [highlightCategory, setHighlightCategory]  = useState(null);

  // Act 3
  const [act3Opacity,       setAct3Opacity]        = useState(0);
  const [activePlatform,    setActivePlatform]     = useState(null);
  const [revealedEdges,     setRevealedEdges]      = useState([]);

  // Act 4
  const [act4Opacity,       setAct4Opacity]        = useState(0);
  const [timelineReveal,    setTimelineReveal]     = useState(0);

  // ── Font injection + unlock scrolling (global CSS locks overflow for other pages)
  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_URL}"]`)) {
      const l = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: FONT_URL });
      document.head.appendChild(l);
    }
    const root = document.getElementById('root');
    const prev = {
      bodyOverflow: document.body.style.overflow,
      bodyHeight: document.body.style.minHeight,
      rootOverflow: root?.style.overflow,
      rootHeight: root?.style.height,
    };
    document.body.style.overflow = 'visible';
    document.body.style.minHeight = 'auto';
    if (root) { root.style.overflow = 'visible'; root.style.height = 'auto'; }
    return () => {
      document.body.style.overflow = prev.bodyOverflow;
      document.body.style.minHeight = prev.bodyHeight;
      if (root) { root.style.overflow = prev.rootOverflow; root.style.height = prev.rootHeight; }
    };
  }, []);

  // ── Auto-scroll Act 4 panel so newly revealed events stay visible
  useEffect(() => {
    const el = act4PanelRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [timelineReveal]);

  // ── Copy swap animation
  useEffect(() => {
    if (isFirst.current) { isFirst.current = false; return; }
    if (!copyRef.current) return;
    gsap.fromTo(copyRef.current, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' });
  }, [copyIndex]);

  // ── All ScrollTriggers
  useEffect(() => {
    const ctx = gsap.context(() => {

      // ACT 1 + ACT 2 unified — long single section:
      //   0 → 0.30   Phase 1: dots / counter / text (counter reaches 300M)
      //   0.30 → 0.82 Phase 2: bubble reveals — 6 categories, one per scroll slot
      //   0.82 → 1.0 Phase 3: linger — all 6 categories fully visible
      const PHASE1_END = 0.30;
      const PHASE2_END = 0.82;
      ScrollTrigger.create({
        trigger: act1Ref.current,
        start: 'top top', end: 'bottom top', scrub: 0.6,
        onUpdate: (self) => {
          const p = self.progress;

          if (p <= PHASE1_END) {
            const phase1 = p / PHASE1_END;
            setCounterProgress(phase1);
            // Find current ACT1_LINES index and compute blend toward the next one.
            // Dots will smoothly drift between consecutive arrangements.
            let idx = 0, blend = 0;
            for (let i = ACT1_LINES.length - 1; i >= 0; i--) {
              if (phase1 >= ACT1_LINES[i].at) {
                idx = i;
                if (i < ACT1_LINES.length - 1) {
                  const span = ACT1_LINES[i + 1].at - ACT1_LINES[i].at;
                  blend = span > 0 ? (phase1 - ACT1_LINES[i].at) / span : 0;
                }
                break;
              }
            }
            setCopyIndex(idx);
            setArrangementPhase(idx);
            setArrangementBlend(Math.max(0, Math.min(1, blend)));
            setBubbleProgress(0);
          } else {
            // Bubble progress completes by PHASE2_END, then stays at 1.0 to linger
            const phase2 = Math.min(1, (p - PHASE1_END) / (PHASE2_END - PHASE1_END));
            setCounterProgress(1);
            setCopyIndex(ACT1_LINES.length - 1);
            setArrangementPhase(5);
            setArrangementBlend(0);
            setBubbleProgress(phase2);
          }
        },
      });

      // ACT 1+2 → ACT 3 crossfade
      ScrollTrigger.create({
        trigger: act3Ref.current,
        start: 'top 80%', end: 'top 20%', scrub: 0.8,
        onUpdate: (self) => {
          setAct1Opacity(1 - self.progress);
          setAct3Opacity(self.progress);
        },
      });

      // ACT 3 platform reveals — each platform lights up as user scrolls.
      // onEnterBack never fires without an `end`, so we handle back-scroll in
      // onLeaveBack: restore the previous platform and trim revealedEdges.
      PLATFORMS.forEach((platform, i) => {
        ScrollTrigger.create({
          trigger: act3Ref.current,
          start: `${8 + i * 18}% center`,
          onEnter: () => {
            setRevealedEdges(prev => prev.includes(platform.id) ? prev : [...prev, platform.id]);
            setActivePlatform(platform.id);
          },
          onLeaveBack: () => {
            const revealed = PLATFORMS.slice(0, i).map(p => p.id);
            setRevealedEdges(revealed);
            setActivePlatform(i > 0 ? PLATFORMS[i - 1].id : null);
          },
        });
      });

      // ACT 3 → ACT 4 crossfade
      ScrollTrigger.create({
        trigger: act4Ref.current,
        start: 'top 75%', end: 'top 25%', scrub: 0.8,
        onUpdate: (self) => {
          setAct3Opacity(1 - self.progress);
          setAct4Opacity(self.progress);
        },
      });

      // ACT 4 timeline reveal — items appear as user scrolls
      // Offset start so first items appear after the crossfade completes
      ScrollTrigger.create({
        trigger: act4Ref.current,
        start: '5% top', end: 'bottom bottom', scrub: 0.5,
        onUpdate: (self) => {
          setTimelineReveal(Math.floor(self.progress * APPROVALS.length));
        },
      });

    }, wrapRef);

    return () => ctx.revert();
  }, []);

  const currentLine  = ACT1_LINES[copyIndex];
  const isBigMoment  = copyIndex === ACT1_LINES.length - 1;
  const activePlatformData = PLATFORMS.find(p => p.id === activePlatform);

  return (
    <div ref={wrapRef} style={{ background: '#07080a', color: '#dde3f0', fontFamily: "'Syne', sans-serif", overflowX: 'clip' }}>

      {/* ══ ACT 1+2: DOT FIELD → STAGED BUBBLE REVEALS ════════════════════════
          One sticky container. Phase 1 = dots/counter/text. Phase 2 = scrolling
          through bubble categories one-by-one. Page never scrolls away from the
          visualization during the reveal. */}
      {(() => null)()}
      <section ref={act1Ref} style={{ height: '1050vh', position: 'relative' }}>
        {(() => {
          // Per-category pull amount — 0 means dots drift freely, 1 means dots
          // of that category have been fully absorbed into the bubbles.
          const N = BUBBLE_REVEAL_ORDER.length;
          const slot = 1 / N;
          const catPulls = BUBBLE_REVEAL_ORDER.map((_, idx) => {
            return Math.max(0, Math.min(1, (bubbleProgress - idx * slot) / slot));
          });

          // Target positions for each category — match bubble cluster centers
          // (CATEGORY_REGIONS uses the same 0-1 normalized coords as the canvas).
          // STABLE_DOT_TARGETS is a module-level constant so DotCanvas doesn't
          // re-assign targets on every render.
          const dotTargets = STABLE_DOT_TARGETS;

          // Identify which category is currently revealing
          const activeIdx = Math.min(N - 1, Math.floor(bubbleProgress * N));
          const activeCategory = bubbleProgress > 0 ? BUBBLE_REVEAL_ORDER[activeIdx] : null;
          const activeColor = activeCategory ? CATEGORY_COLORS[activeCategory] : '#00e5b0';

          // Running tally — counts the currently-revealing category, so it
          // reaches 6/6 once you're scrolling through the final slot.
          const revealedCount = bubbleProgress <= 0 ? 0 : Math.min(N, Math.ceil(bubbleProgress * N));

          return (
            <div style={{
              position: 'sticky', top: 0, height: '100vh',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', opacity: act1Opacity, transition: 'opacity 0.1s',
            }}>
              {/* Dot canvas — dots get pulled toward their category's bubble cluster */}
              <div style={{ position: 'absolute', inset: 0 }}>
                <DotCanvas arrangementPhase={arrangementPhase} arrangementBlend={arrangementBlend} catPulls={catPulls} targets={dotTargets} />
              </div>
              <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 70% at 50% 50%, rgba(7,8,10,0.3) 0%, rgba(7,8,10,0.72) 100%)', pointerEvents: 'none', opacity: Math.max(0, 1 - bubbleProgress * 2) }} />

              {/* Counter + Act 1 text wrapper — fades out when bubble phase starts.
                  Note: GSAP animates copyRef directly so we wrap it in an outer
                  div whose opacity React controls. */}
              <div style={{ opacity: Math.max(0, 1 - bubbleProgress * 12), display: 'contents' }}>
                <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', marginBottom: '1.5rem', opacity: Math.max(0, 1 - bubbleProgress * 12) }}>
                  <Counter value={counterProgress} />
                </div>
                <div style={{ position: 'relative', zIndex: 2, maxWidth: '560px', textAlign: 'center', padding: '0 2rem', opacity: Math.max(0, 1 - bubbleProgress * 12) }}>
                  <div ref={copyRef}>
                    <p style={{
                      ...( isBigMoment ? S.serif : S.mono ),
                      fontSize: isBigMoment ? 'clamp(1.4rem, 3vw, 2.2rem)' : 'clamp(0.8rem, 1.5vw, 1rem)',
                      lineHeight: isBigMoment ? 1.25 : 1.85,
                      color: isBigMoment ? '#dde3f0' : '#5a637a',
                      whiteSpace: 'pre-line',
                    }}>
                      {currentLine.text}
                    </p>
                  </div>
                </div>
              </div>
              {copyIndex === 0 && bubbleProgress === 0 && (
                <div style={{ position: 'absolute', bottom: '2.5rem', ...S.mono, fontSize: '0.6rem', letterSpacing: '0.2em', color: '#3a4255', textTransform: 'uppercase', animation: 'rsBounce 2s ease infinite' }}>
                  ↓ scroll
                </div>
              )}

              {/* Bubble transform — categories appear one at a time across the bubble phase */}
              {bubbleProgress > 0 && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}>
                  <div style={{ width: '90%', height: '85%', maxWidth: '1200px' }}>
                    <BubbleTransform transformProgress={bubbleProgress} highlightCategory={highlightCategory} />
                  </div>
                </div>
              )}

              {/* Phase 2 overlay — current category label + reveal count.
                  When all 6 are revealed, swap to a celebrate-state header. */}
              {bubbleProgress > 0 && (() => {
                const allRevealed = bubbleProgress >= 0.999;
                return (
                  <>
                    {/* Top label */}
                    <div style={{
                      position: 'absolute', top: '6vh', left: 0, right: 0,
                      textAlign: 'center', zIndex: 10, pointerEvents: 'none',
                      opacity: Math.min(1, bubbleProgress * 4),
                    }}>
                      {allRevealed ? (
                        <>
                          <div style={{ ...S.label, marginBottom: '0.6rem', color: '#00e5b0' }}>
                            7,000 conditions · 6 disease families
                          </div>
                          <div style={{ ...S.serif, fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', color: '#dde3f0', letterSpacing: '0.01em' }}>
                            Every bubble is a life sentence <em style={{ color: '#00e5b0', fontStyle: 'italic' }}>without a cure.</em>
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ ...S.label, marginBottom: '0.6rem', color: activeColor }}>
                            Category {activeIdx + 1} of {N}
                          </div>
                          <div style={{ ...S.serif, fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', color: activeColor, letterSpacing: '0.01em' }}>
                            {activeCategory}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Bottom: framing copy */}
                    <div style={{
                      position: 'absolute', bottom: '5vh', left: 0, right: 0,
                      textAlign: 'center', zIndex: 10, pointerEvents: 'none',
                      opacity: Math.min(1, bubbleProgress * 4),
                    }}>
                      <p style={{ ...S.mono, fontSize: 'clamp(0.75rem, 1.2vw, 0.88rem)', ...S.muted, maxWidth: '520px', margin: '0 auto', lineHeight: 1.85 }}>
                        Bubble size reflects US patient population. 95% have no approved treatment.
                      </p>
                      <div style={{
                        ...S.mono, fontSize: '0.65rem', letterSpacing: '0.2em',
                        color: allRevealed ? '#00e5b0' : '#3a4255',
                        marginTop: '1rem', textTransform: 'uppercase',
                        transition: 'color 0.4s',
                      }}>
                        {revealedCount} of {N} categories revealed
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          );
        })()}
      </section>

      {/* Bridge 2→3 */}
      <Bridge
        eyebrow="But something is changing"
        heading="Most of these diseases<br/><em style='color:#00e5b0;font-style:italic'>share the same broken logic.</em>"
        sub="A therapy proven in one disease is a blueprint for the next. The science doesn't start over — it compounds."
      />

      {/* ══ ACT 3: PLATFORM COVERAGE ═════════════════════════════════════════ */}
      <section ref={act3Ref} style={{ height: '400vh', position: 'relative' }}>
        <div style={{
          position: 'sticky', top: 0, height: '100vh',
          display: 'grid', gridTemplateColumns: '2fr 1fr', alignItems: 'stretch',
          overflow: 'hidden', opacity: act3Opacity, transition: 'opacity 0.15s',
        }}>
          {/* Left: platform × disease treatment matrix */}
          <div style={{ position: 'relative', height: '100%', minHeight: 0, overflow: 'hidden', padding: '4.5rem 1.5rem 5rem 3rem' }}>
            <TreatmentMatrix activePlatform={activePlatform} revealedEdges={revealedEdges} />
            {/* Running coverage tally — bottom-left, never collides with anchor */}
            {activePlatformData && (() => {
              const treatedSet = new Set();
              revealedEdges.forEach(pid => {
                const p = PLATFORMS.find(pp => pp.id === pid);
                if (p) p.diseases.forEach(d => treatedSet.add(d));
              });
              return (
                <div style={{
                  position: 'absolute', bottom: '3vh', left: '3rem',
                  zIndex: 5, pointerEvents: 'none',
                  display: 'flex', alignItems: 'baseline', gap: '1.2rem',
                  animation: 'rsFadeUp 0.4s ease',
                }}>
                  <div>
                    <div style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '0.2em', color: '#3a4255', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                      Platforms revealed
                    </div>
                    <div style={{ ...S.mono, fontSize: '1.2rem', color: activePlatformData.color, letterSpacing: '0.04em' }}>
                      {revealedEdges.length}<span style={{ fontSize: '0.65em', color: '#3a4255' }}> / {PLATFORMS.length}</span>
                    </div>
                  </div>
                  <div style={{ width: '1px', height: '34px', background: 'rgba(255,255,255,0.08)' }} />
                  <div>
                    <div style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '0.2em', color: '#3a4255', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                      Diseases reached
                    </div>
                    <div style={{ ...S.mono, fontSize: '1.2rem', color: '#dde3f0', letterSpacing: '0.04em' }}>
                      {treatedSet.size}<span style={{ fontSize: '0.65em', color: '#3a4255' }}> / {DISEASES.length}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Right: platform detail panel */}
          <div style={{ padding: '3rem 5vw 3rem 2rem', borderLeft: '1px solid rgba(255,255,255,0.05)', height: '100%', minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={S.label}>The Overlap</div>
            {!activePlatformData ? (
              <div>
                <h2 style={{ ...S.serif, fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', lineHeight: 1.1, marginBottom: '1.2rem' }}>
                  Look closer at what these diseases<br />share.
                </h2>
                <p style={{ ...S.mono, fontSize: '0.8rem', lineHeight: 1.85, ...S.muted }}>
                  Not at what makes them different — at what makes them the same. A small number of fundamental strategies can solve them all.
                </p>
              </div>
            ) : (
              <div style={{ animation: 'rsFadeUp 0.4s ease' }}>
                <div style={{ display: 'inline-block', ...S.mono, fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '0.3rem 0.8rem', border: `1px solid ${activePlatformData.color}40`, background: `${activePlatformData.color}12`, color: activePlatformData.color, marginBottom: '1rem', borderRadius: '2px' }}>
                  Therapy Platform
                </div>
                <h2 style={{ ...S.serif, fontSize: 'clamp(1.6rem, 2.5vw, 2.4rem)', lineHeight: 1.1, marginBottom: '0.8rem', color: activePlatformData.color }}>
                  {activePlatformData.name.replace('\n', ' ')}
                </h2>
                <div style={{ ...S.mono, fontSize: '0.68rem', color: activePlatformData.color, letterSpacing: '0.06em', marginBottom: '1rem', lineHeight: 1.5, opacity: 0.8 }}>
                  ↳ {activePlatformData.proof}
                </div>
                <p style={{ ...S.mono, fontSize: '0.78rem', lineHeight: 1.85, ...S.muted, marginBottom: '1.5rem' }}>
                  {activePlatformData.desc}
                </p>
                <div style={{ ...S.mono, fontSize: '0.65rem', letterSpacing: '0.1em', color: '#3a4255', textTransform: 'uppercase', marginBottom: '0.6rem' }}>
                  Connected diseases
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {activePlatformData.diseases.map(d => (
                    <span key={d} style={{ ...S.mono, fontSize: '0.62rem', padding: '0.2rem 0.6rem', border: `1px solid ${activePlatformData.color}30`, color: activePlatformData.color, background: `${activePlatformData.color}08`, borderRadius: '2px' }}>
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bridge 3→4 */}
      <Bridge
        eyebrow="The momentum"
        heading="Every approval is a door<br/><em style='color:#00e5b0;font-style:italic'>opened for the next one.</em>"
        sub="For most of medical history, rare diseases had no treatment and no prospect of one. That is changing — faster than anyone predicted."
      />

      {/* ══ ACT 4: TIMELINE ═══════════════════════════════════════════════ */}
      <section ref={act4Ref} style={{ minHeight: '400vh', position: 'relative' }}>
        <div style={{
          position: 'sticky', top: 0, height: '100vh',
          overflow: 'hidden', opacity: act4Opacity, transition: 'opacity 0.15s',
          display: 'grid', gridTemplateColumns: '1fr 2fr', alignItems: 'start',
        }}>
          {/* Left: fixed copy */}
          <div style={{ padding: '6rem 3rem 4rem 6vw', position: 'sticky', top: 0 }}>
            <div style={S.label}>Milestones</div>
            <h2 style={{ ...S.serif, fontSize: 'clamp(2rem, 3vw, 2.8rem)', lineHeight: 1.1, marginBottom: '1.5rem' }}>
              Forty years of breakthroughs. Accelerating.
            </h2>
            <p style={{ ...S.mono, fontSize: '0.78rem', lineHeight: 1.85, ...S.muted, marginBottom: '2rem' }}>
              In 1983, the Orphan Drug Act created the first financial reason to develop rare disease therapies. What followed was slow, then fast, then faster.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {[['#00e5b0','AAV Gene Therapy'],['#b48cfa','ASO Platform'],['#f5a623','CRISPR'],['#ff6b8a','Enzyme Replacement'],['#5bc8f5','mRNA / RNAi'],['#5a637a','Policy']].map(([col, lbl]) => (
                <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: col, flexShrink: 0 }} />
                  <span style={{ ...S.mono, fontSize: '0.68rem', color: '#5a637a', letterSpacing: '0.06em' }}>{lbl}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '3rem', padding: '1.2rem', background: 'rgba(0,229,176,0.04)', border: '1px solid rgba(0,229,176,0.15)' }}>
              <div style={{ ...S.mono, fontSize: '0.65rem', color: '#00e5b0', marginBottom: '0.4rem', letterSpacing: '0.1em' }}>2020 – 2024</div>
              <div style={{ ...S.mono, fontSize: '0.78rem', ...S.muted, lineHeight: 1.6 }}>
                More gene and cell therapies approved than in the entire previous decade combined.
              </div>
            </div>
          </div>

          {/* Right: scrolling timeline — ref enables auto-scroll as events reveal */}
          <div ref={act4PanelRef} style={{ padding: '4rem 6vw 4rem 2rem', overflowY: 'auto', height: '100vh' }}>
            <Timeline revealCount={timelineReveal} />
          </div>
        </div>
      </section>

      {/* ══ CLOSING ═══════════════════════════════════════════════════════ */}
      <div style={{ textAlign: 'center', padding: '10rem 2rem 8rem', background: 'linear-gradient(to bottom, #07080a 0%, #0b0f1a 100%)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <p style={{ ...S.mono, fontSize: '0.6rem', letterSpacing: '0.2em', color: '#00e5b0', textTransform: 'uppercase', marginBottom: '2rem' }}>
          The only question
        </p>
        <p style={{ ...S.serif, fontSize: 'clamp(2.5rem, 6vw, 5.5rem)', lineHeight: 1.0, maxWidth: '800px', margin: '0 auto 2rem', letterSpacing: '-0.02em' }}>
          300 million people<br />
          <em style={{ color: '#00e5b0', fontStyle: 'italic' }}>are waiting.</em>
        </p>
        <p style={{ ...S.mono, fontSize: 'clamp(0.8rem, 1.4vw, 1rem)', ...S.muted, maxWidth: '480px', margin: '0 auto 3rem', lineHeight: 1.85 }}>
          The science is ready. The platforms exist. Every rare disease solved is a door opened for the next one.
        </p>
        <p style={{ ...S.serif, fontSize: 'clamp(1rem, 2vw, 1.5rem)', color: '#3a4255', fontStyle: 'italic' }}>
          How fast do we move?
        </p>
      </div>

      {/* ══ HOW YOU CAN HELP ══════════════════════════════════════════════ */}
      <div style={{ background: 'linear-gradient(to bottom, #0b0f1a 0%, #0d1422 100%)', borderTop: '1px solid rgba(0,229,176,0.18)', padding: '7rem 2rem' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <div style={{ ...S.label, marginBottom: '1.2rem' }}>How You Can Help</div>
          <p style={{ ...S.serif, fontSize: 'clamp(2rem, 5vw, 3.6rem)', lineHeight: 1.08, letterSpacing: '-0.02em', marginBottom: '1.6rem' }}>
            Rare is only rare<br />
            <em style={{ color: '#00e5b0', fontStyle: 'italic' }}>until it's someone you love.</em>
          </p>
          <p style={{ ...S.mono, fontSize: '0.8rem', ...S.muted, lineHeight: 1.9, marginBottom: '3.5rem', maxWidth: '560px' }}>
            Nearly every breakthrough in rare disease was pushed forward by patients and families who refused to wait. You can be part of the next one.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.6rem', marginBottom: '4rem' }}>
            {[
              { t: 'Give', d: 'Fund research and direct family support. Small recurring gifts compound into trials.' },
              { t: 'Join a registry', d: 'Patient data is what makes clinical trials possible. Your information accelerates the next therapy.' },
              { t: 'Amplify', d: 'Share these stories. Awareness is what drives the funding and policy that follow.' },
            ].map((w) => (
              <div key={w.t} style={{ borderTop: '2px solid #00e5b0', paddingTop: '1rem' }}>
                <div style={{ ...S.syne, fontSize: '1rem', fontWeight: 600, color: '#dde3f0', marginBottom: '0.5rem' }}>{w.t}</div>
                <div style={{ ...S.mono, fontSize: '0.72rem', color: '#5a637a', lineHeight: 1.8 }}>{w.d}</div>
              </div>
            ))}
          </div>

          <div style={{ ...S.label, marginBottom: '1.2rem', color: '#5a637a' }}>Foundations Worth Supporting</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {[
              { name: 'National Organization for Rare Disorders (NORD)', desc: 'Patient assistance programs, advocacy, and research grants.', url: 'https://rarediseases.org' },
              { name: 'Global Genes', desc: 'Connects, equips, and empowers the rare disease community.', url: 'https://globalgenes.org' },
              { name: 'EveryLife Foundation for Rare Diseases', desc: 'Drives science-based public policy for rare disease patients.', url: 'https://everylifefoundation.org' },
              { name: 'Rare Diseases International', desc: 'The global voice advocating for rare disease patients worldwide.', url: 'https://www.rarediseasesinternational.org' },
            ].map((f) => (
              <a key={f.name} href={f.url} target="_blank" rel="noopener noreferrer"
                 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem', padding: '1.3rem 1.5rem', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', textDecoration: 'none', transition: 'border-color 0.18s ease, background 0.18s ease' }}
                 onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(0,229,176,0.5)'; e.currentTarget.style.background = 'rgba(0,229,176,0.05)'; }}
                 onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}>
                <div>
                  <div style={{ ...S.syne, fontSize: '0.9rem', fontWeight: 600, color: '#dde3f0', marginBottom: '0.3rem' }}>{f.name}</div>
                  <div style={{ ...S.mono, fontSize: '0.7rem', color: '#5a637a', lineHeight: 1.65 }}>{f.desc}</div>
                </div>
                <span style={{ ...S.mono, fontSize: '1rem', color: '#00e5b0', flexShrink: 0 }}>&rarr;</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ══ SOURCES & NOTES ═══════════════════════════════════════════════ */}
      <div style={{ background: '#0b0f1a', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '5rem 2rem 6rem' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <div style={{ ...S.label, marginBottom: '1.5rem' }}>Sources &amp; Notes</div>
          <p style={{ ...S.mono, fontSize: '0.72rem', ...S.muted, lineHeight: 1.8, marginBottom: '2rem' }}>
            Figures on this page are rounded for clarity and reflect widely-cited
            estimates. Rare-disease prevalence and patient counts vary by source
            and methodology.
          </p>
          <ol style={{ ...S.mono, fontSize: '0.72rem', ...S.muted, lineHeight: 1.9, paddingLeft: '1.4rem', margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
            {SOURCES.map((s, i) => (
              <li key={i} style={{ paddingLeft: '0.3rem' }}>
                <span style={{ color: '#7a8294' }}>{s.claim}</span>
                {' — '}
                {s.url ? (
                  <a href={s.url} target="_blank" rel="noopener noreferrer"
                     style={{ color: '#00e5b0', textDecoration: 'none', borderBottom: '1px solid rgba(0,229,176,0.3)' }}>
                    {s.cite}
                  </a>
                ) : (
                  <span style={{ color: '#9aa3b6' }}>{s.cite}</span>
                )}
              </li>
            ))}
          </ol>
        </div>
      </div>

      <ContactFooter />

      <style>{`
        @keyframes rsBounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(6px)} }
        @keyframes rsFadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeInUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}
